import { Box, Typography } from '@mui/material'
import CellHOC from '@platform/react/hoc/list/cell'
import { def } from 'lib/util'
import { empty } from 'lib/util/object'
import internal from 'model/organisation/collection/internal'

/**
 * Special organisation name styles
 *
 * @param theme
 *
 * @returns {
 * {subRow: {alignItems: string, gap: string, display: string},
 * root: {overflow: string, flexDirection: string, display: string, justifyContent: string},
 * name: {fontWeight: number}, type: {color}, validation: {color}}
 * }
 */
const styles = theme => ({
  root: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
  },
  subRow: {
    gap: '5px',
    display: 'flex',
    alignItems: 'center',
  },
  name: {
    fontWeight: 700,
  },
  type: {
    color: theme.palette.info.main,
  },
  validation: {
    color: theme.palette.signal.main,
  },
  duplicateOf: {
    color: theme.palette.warning.main,
  },
})

/**
 * Intended to display the name of the organisation alongside a status. Also attempts to display
 * additional information about the organisation, like `toBeValidated` if the user in question
 * passed the acl check.
 *
 * @param {Object} props    element's properties
 * @param {Object} classes  element's style classes
 *
 * @returns {JSX.Element}
 */
const ListItemCellOrganisationWithStatus = (classes, props = {}) => {
  const {
    acl,
    type,
    name,
    typeInfo,
    number,
    parent: { key: parentId = null, trees: { parent: baseParentIds = [] } = {} } = {},
    duplicateOf = {},
    toBeValidated,
    duplicateOfInfo,
    supportUnitInfo,
    toBeApprovedInfo,
    toBeValidatedInfo,
    support: { provides = null } = {},
  } = props

  const ids = [parentId, ...baseParentIds]

  const displayApproval = internal.some(x => ids.includes(x.key))
  const displayValidation = def(toBeValidated) && acl !== false

  const subRow = []
  const organisationType = provides
    ? supportUnitInfo : typeInfo?.[type] || null

  organisationType
    && subRow.push({ class: classes.type, value: organisationType })

  number
  && subRow.push({ class: classes.type, value: number })

  displayValidation
    && subRow.push({ class: classes.validation, value: toBeValidatedInfo })

  !displayValidation
    && displayApproval
    && subRow.push({ class: classes.validation, value: toBeApprovedInfo })

  !displayValidation
    && !displayApproval
    && !empty(duplicateOf)
    && subRow.push({
      class: classes.duplicateOf,
      value: duplicateOfInfo,
      suffix: duplicateOf.value?.name,
    })

  return (
    <>
      <Typography className={`${classes.row} ${classes.name}`}>
        {name}
      </Typography>
      <Box className={`${classes.subRow}`}>
        {subRow.map((item, index) => (
          <Typography
            key={index}
            variant={'caption'}
            color={'textSecondary'}
            className={`${classes.row} ${item.class}`}
          >
            {item.value} {item?.suffix}
          </Typography>
        )).reduce((result, item) => (result.length ? [result, ' ● ', item] : [item]), [])}
      </Box>
    </>
  )
}

export default CellHOC(ListItemCellOrganisationWithStatus, styles)
